// Phase 5Z.20 — Sicherheits-Dashboard (Security / SOC Overview)
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ShieldAlert, Lock, Unlock, KeyRound, Users, AlertTriangle,
  Activity, Globe, RefreshCw, ShieldCheck,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";

function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" });
  } catch { return ts; }
}

function StatCard({ icon: Icon, label, value, tone = "zinc", testid }) {
  const tones = {
    zinc: "border-zinc-800 bg-zinc-950 text-zinc-100",
    amber: "border-amber-500/40 bg-amber-500/5 text-amber-200",
    red: "border-red-500/40 bg-red-500/5 text-red-200",
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200",
    sky: "border-sky-500/40 bg-sky-500/5 text-sky-200",
  };
  return (
    <div data-testid={testid} className={`rounded-lg border p-4 ${tones[tone] || tones.zinc}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-3xl font-display font-bold tabular-nums">{value}</div>
    </div>
  );
}

export default function SecurityDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restricted to admin / geschaeftsfuehrung
  const allowed = user?.is_super_admin || user?.role === "admin" || user?.role === "geschaeftsfuehrung";

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/security/overview");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (allowed) load(); }, [allowed]);

  const stats = data?.portal_stats || {};
  const lockedCount = data?.locked_count ?? 0;
  const hotspots = data?.ip_hotspots_24h || [];
  const recentFails = data?.recent_failed_attempts || [];
  const recentEvents = data?.recent_events || [];
  const locked = data?.locked_accounts || [];

  const totalFailures24h = useMemo(
    () => hotspots.reduce((s, x) => s + (x.attempts || 0), 0),
    [hotspots],
  );

  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      <ModuleHint moduleKey="security" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-50 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-500" />
            Sicherheits-Dashboard
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Live-Telemetrie zu Anmeldeversuchen, gesperrten Konten und Portal-Zugängen.
            Generiert: {fmt(data?.generated_at)}
          </p>
        </div>
        <button
          onClick={load}
          data-testid="security-refresh-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-700 text-zinc-200 text-sm hover:bg-zinc-900 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-200" data-testid="security-error">
          <AlertTriangle className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          {String(error)}
        </div>
      )}

      {/* ────────── Top stat cards ────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testid="stat-locked" icon={Lock} tone={lockedCount > 0 ? "red" : "emerald"}
          label="Gesperrte Konten" value={lockedCount} />
        <StatCard testid="stat-fails-24h" icon={Activity} tone={totalFailures24h > 0 ? "amber" : "zinc"}
          label="Fehlversuche 24h" value={totalFailures24h} />
        <StatCard testid="stat-active-drivers" icon={Users}
          label="Aktive Fahrer" value={stats.active_drivers ?? 0} />
        <StatCard testid="stat-portal-enabled" icon={ShieldCheck} tone="sky"
          label="Portal aktiv" value={stats.portal_enabled ?? 0} />
        <StatCard testid="stat-must-change" icon={KeyRound} tone={stats.must_change_password > 0 ? "amber" : "zinc"}
          label="Passwort-Wechsel offen" value={stats.must_change_password ?? 0} />
        <StatCard testid="stat-auto-disabled" icon={Unlock}
          label="Auto-deaktiviert" value={stats.auto_disabled ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ────────── Locked accounts ────────── */}
        <Section
          title="Gesperrte Konten"
          icon={Lock}
          subtitle={`${locked.length} aktiv gesperrt — Brute-Force-Schutz`}
          testid="locked-accounts-section"
        >
          {locked.length === 0 ? (
            <Empty text="Keine gesperrten Konten." />
          ) : (
            <Table headers={["Benutzer", "Mitarbeiter", "IP", "Gesperrt bis"]}>
              {locked.map((l, i) => (
                <tr key={i} className="border-t border-zinc-800" data-testid={`locked-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-100">{l.username || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300">
                    {l.employee_name || "—"}
                    {l.department && <span className="text-zinc-500"> · {l.department}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">{l.ip || "—"}</td>
                  <td className="px-3 py-2 text-xs text-amber-300">{fmt(l.locked_until)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        {/* ────────── IP hotspots ────────── */}
        <Section
          title="IP-Hotspots (24h)"
          icon={Globe}
          subtitle="Top-10 IPs mit den meisten Fehlversuchen"
          testid="ip-hotspots-section"
        >
          {hotspots.length === 0 ? (
            <Empty text="Keine Fehlversuche in den letzten 24h." />
          ) : (
            <Table headers={["IP-Adresse", "Versuche"]}>
              {hotspots.map((h, i) => (
                <tr key={i} className="border-t border-zinc-800" data-testid={`hotspot-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-100">{h.ip}</td>
                  <td className="px-3 py-2 tabular-nums text-zinc-300">{h.attempts}</td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        {/* ────────── Recent failed attempts ────────── */}
        <Section
          title="Letzte Fehlversuche"
          icon={Activity}
          subtitle={`${recentFails.length} Einträge — neueste zuerst`}
          testid="recent-fails-section"
        >
          {recentFails.length === 0 ? (
            <Empty text="Keine Fehlversuche registriert." />
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table headers={["Benutzer", "IP", "Versuche", "Letzter", "Status"]}>
                {recentFails.slice(0, 50).map((f, i) => (
                  <tr key={i} className="border-t border-zinc-800" data-testid={`fail-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-100">{f.username || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-400">{f.ip || "—"}</td>
                    <td className="px-3 py-2 tabular-nums text-zinc-300">{f.failures}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400">{fmt(f.last_failure)}</td>
                    <td className="px-3 py-2">
                      {f.is_locked ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] uppercase tracking-wider">
                          <Lock className="w-3 h-3" /> Gesperrt
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">offen</span>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
        </Section>

        {/* ────────── Security events ────────── */}
        <Section
          title="Letzte Security-Events"
          icon={ShieldCheck}
          subtitle="Portal-Aktivierungen, Deaktivierungen, Passwort-Resets"
          testid="recent-events-section"
        >
          {recentEvents.length === 0 ? (
            <Empty text="Keine Events vorhanden." />
          ) : (
            <Table headers={["Zeit", "Aktion", "Benutzer", "Detail"]}>
              {recentEvents.map((e, i) => (
                <tr key={i} className="border-t border-zinc-800" data-testid={`event-row-${i}`}>
                  <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{fmt(e.timestamp)}</td>
                  <td className="px-3 py-2 text-xs text-zinc-300 font-mono">{e.action}</td>
                  <td className="px-3 py-2 text-xs text-zinc-300">
                    {e.user_name || "—"}
                    {e.user_role && <span className="text-zinc-500"> · {e.user_role}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-300">{e.summary}</td>
                </tr>
              ))}
            </Table>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, subtitle, children, testid }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-amber-500" />
          <h2 className="font-display font-semibold text-zinc-100">{title}</h2>
        </div>
        {subtitle && <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-zinc-900/40">
          {headers.map((h, i) => (
            <th key={i} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function Empty({ text }) {
  return (
    <div className="px-4 py-8 text-center text-sm text-zinc-500">{text}</div>
  );
}
